/**
 * Screenshot Service for AI Chat
 *
 * SOLID Architecture:
 * - S: Single responsibility - only screenshot generation
 * - O: Open for extension via ScreenshotProgressHandler
 * - L: N/A (no inheritance)
 * - I: Segregated interfaces for Service and ProgressHandler
 * - D: Depends on abstractions (ScreenshotProgressHandler), not concretions
 */

import type { EditorHost } from '@blocksuite/affine/std';
import {
  GfxControllerIdentifier,
  type GfxModel,
  GfxPrimitiveElementModel,
} from '@blocksuite/affine/std/gfx';
import { Bound, getCommonBound } from '@blocksuite/global/gfx';

// ============================================================================
// Types & Interfaces
// ============================================================================

/**
 * Screenshot size categories
 */
export type ScreenshotSizeCategory = 'normal' | 'optimized' | 'denied';

/**
 * Result of size validation
 */
export interface ScreenshotSizeValidation {
  category: ScreenshotSizeCategory;
  width: number;
  height: number;
  scale?: number;
}

/**
 * Screenshot generation result
 */
export interface ScreenshotResult {
  file: File;
  validation: ScreenshotSizeValidation;
}

/**
 * Configuration for screenshot service
 */
export interface ScreenshotConfig {
  /** Below this: no optimization needed (default: 4096) */
  normalThreshold: number;
  /** Above this: denied (default: 20000) */
  maxThreshold: number;
  /** Padding around the screenshot (default: 5) */
  padding: number;
}

/**
 * Progress handler interface (Dependency Inversion)
 * UI components implement this to receive progress updates
 */
export interface ScreenshotProgressHandler {
  /**
   * Called when screenshot generation starts
   * @param validation - Size validation result with category and dimensions
   */
  onStart(validation: ScreenshotSizeValidation): void;

  /**
   * Called when screenshot generation ends (success or failure)
   */
  onEnd(): void;

  /**
   * Called when an error occurs
   * @param error - The error that occurred
   */
  onError(error: ScreenshotError): void;
}

/**
 * Screenshot service interface
 */
export interface IScreenshotService {
  /**
   * Validate selection size without generating screenshot
   */
  validateSize(elements: GfxModel[]): ScreenshotSizeValidation;

  /**
   * Generate screenshot from selected elements
   * @throws ScreenshotTooLargeError if selection exceeds max threshold
   */
  generate(host: EditorHost): Promise<ScreenshotResult>;

  /**
   * Set progress handler for UI feedback
   */
  setProgressHandler(handler: ScreenshotProgressHandler | null): void;
}

// ============================================================================
// Errors
// ============================================================================

/**
 * Base error for screenshot operations
 */
export abstract class ScreenshotError extends Error {
  abstract readonly i18nKey: string;
}

/**
 * Error thrown when selection is too large
 */
export class ScreenshotTooLargeError extends ScreenshotError {
  readonly i18nKey = 'com.streamlain.ai.screenshot.selection-too-large';
  readonly width: number;
  readonly height: number;

  constructor(width: number, height: number, maxThreshold: number) {
    super(
      `Selection too large for screenshot: ${Math.round(width)}x${Math.round(height)}. ` +
        `Maximum allowed: ${maxThreshold}x${maxThreshold}`
    );
    this.name = 'ScreenshotTooLargeError';
    this.width = width;
    this.height = height;
  }
}

// ============================================================================
// Default Configuration
// ============================================================================

const DEFAULT_CONFIG: ScreenshotConfig = {
  normalThreshold: 4096,
  maxThreshold: 20000,
  padding: 5,
};

// ============================================================================
// Screenshot Service Implementation
// ============================================================================

/**
 * Screenshot service for generating screenshots from edgeless selections
 */
export class ScreenshotService implements IScreenshotService {
  private readonly config: ScreenshotConfig;
  private progressHandler: ScreenshotProgressHandler | null = null;

  constructor(config: Partial<ScreenshotConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Set progress handler for UI feedback
   */
  setProgressHandler(handler: ScreenshotProgressHandler | null): void {
    this.progressHandler = handler;
  }

  /**
   * Validate selection size and return category
   */
  validateSize(elements: GfxModel[]): ScreenshotSizeValidation {
    if (elements.length === 0) {
      return { category: 'normal', width: 0, height: 0 };
    }

    // Calculate combined bounds of all elements
    const bounds = elements
      .map(element => {
        if ('xywh' in element && element.xywh) {
          return Bound.deserialize(element.xywh as string);
        }
        if ('elementBound' in element) {
          return (element as GfxPrimitiveElementModel).elementBound;
        }
        return null;
      })
      .filter((b): b is Bound => b !== null);

    if (bounds.length === 0) {
      return { category: 'normal', width: 0, height: 0 };
    }

    const combinedBound = getCommonBound(bounds);
    if (!combinedBound) {
      return { category: 'normal', width: 0, height: 0 };
    }

    const { w: width, h: height } = combinedBound;

    // Category 3: Denied - too large
    if (width > this.config.maxThreshold || height > this.config.maxThreshold) {
      return { category: 'denied', width, height };
    }

    // Category 2: Optimized - needs scaling
    if (
      width > this.config.normalThreshold ||
      height > this.config.normalThreshold
    ) {
      const maxDimension = Math.max(width, height);
      const scale = this.config.normalThreshold / maxDimension;
      return { category: 'optimized', width, height, scale };
    }

    // Category 1: Normal - no changes needed
    return { category: 'normal', width, height };
  }

  /**
   * Generate screenshot from current selection
   */
  async generate(host: EditorHost): Promise<ScreenshotResult> {
    const gfx = host.std.get(GfxControllerIdentifier);
    const selectedElements = gfx.selection.selectedElements;

    // Validate size
    const validation = this.validateSize(selectedElements);

    // Handle denied case
    if (validation.category === 'denied') {
      const error = new ScreenshotTooLargeError(
        validation.width,
        validation.height,
        this.config.maxThreshold
      );
      this.progressHandler?.onError(error);
      throw error;
    }

    // Notify start for optimized case
    if (validation.category === 'optimized') {
      this.progressHandler?.onStart(validation);
      // Give UI time to render before heavy computation
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    try {
      // Import and use the canvas generation from selection-utils
      const { selectedToCanvas } = await import('../utils/selection-utils');
      const canvas = await selectedToCanvas(host);

      if (!canvas) {
        throw new Error('Failed to generate canvas');
      }

      // Convert canvas to blob
      const blob = await new Promise<Blob | null>(resolve =>
        canvas.toBlob(resolve)
      );

      if (!blob) {
        throw new Error('Failed to convert canvas to blob');
      }

      const file = new File([blob], 'selected.png', { type: 'image/png' });

      return { file, validation };
    } finally {
      // Always notify end
      if (validation.category === 'optimized') {
        this.progressHandler?.onEnd();
      }
    }
  }
}

// ============================================================================
// Singleton Instance (for convenience, but DI is preferred)
// ============================================================================

let defaultInstance: ScreenshotService | null = null;

/**
 * Get default screenshot service instance
 * Prefer using dependency injection instead
 */
export function getScreenshotService(): ScreenshotService {
  if (!defaultInstance) {
    defaultInstance = new ScreenshotService();
  }
  return defaultInstance;
}
